import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';
import { RouterLink } from '@redux-first-router-link';

import { Grid, Box, Heading, Button, useTheme } from '@simplywallst/ui-core';

import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';

import { ROUTE_PORTFOLIO_NEW } from '@/constants/routes';

import { useAggregatePortfolio } from '@/hooks/portfolio/useAggregatePortfolio';

import { AggregatePortfolioPerformance } from '../AggregatePortfolioPerformance';
import { AggregatePortfolioSnowflake } from '../AggregatePortfolioSnowflake';
import { LoadingState } from './LoadingState';
import { PortfolioDropdown } from '../../../../components/PortfolioDropdown';

const PortfolioLimitsBanner = loadable(
  () => import('../../../../components/PortfolioLimitsBanner'),
  {
    resolveComponent: (c) => c.PortfolioLimitsBanner,
  }
);

export const AggregatePortfolioOverview = () => {
  const theme = useTheme();
  const { trackEvent } = useTracking({
    type: 'track',
    subject: 'page',
  });

  const { data: aggregatePortfolio, status } = useAggregatePortfolio();

  const isLoading = status === 'pending';
  const isError = status === 'error';

  if (isLoading) return <LoadingState />;
  if (isError || !aggregatePortfolio) return null;

  const shouldShowPortfolioLimitsBanner =
    aggregatePortfolio?.analysis.isAnalysisRestricted;

  return (
    <Grid
      gap="x2"
      templateRows={`repeat(${shouldShowPortfolioLimitsBanner ? 3 : 2}, auto)`}
    >
      <Grid
        as="header"
        data-cy-id="my-portfolios-header"
        justifyContent="space-between"
        alignItems="center"
        paddingXLg="x0"
        paddingY="x2"
        paddingX="x2"
        templateColumns="max-content max-content"
      >
        <PortfolioDropdown
          portfolioId={null}
          onClick={() => {
            trackEvent({
              modifier: 'portfolioDropDown',
              action: 'click',
            });
          }}
        >
          <Heading as="h1" typography="megaStrong" isTruncated>
            All Portfolios
          </Heading>
        </PortfolioDropdown>
        <RouterLink to={{ type: ROUTE_PORTFOLIO_NEW }}>
          <Button styleType="louder" alignIcon="left" onClick={() => {}}>
            <PlusSolid fill={theme.color.brand01} /> New portfolio
          </Button>
        </RouterLink>
      </Grid>

      {shouldShowPortfolioLimitsBanner && (
        <Box marginBottom="x2" minHeight="56px">
          <PortfolioLimitsBanner
            data-cy-id="portfolio-limits-banner"
            content="Some of your portfolios are limited by your plan, affecting the total metrics."
          />
        </Box>
      )}
      <Grid
        templateRows="auto auto"
        templateRowsMd="auto"
        templateColumnsMd="auto 32%"
        gap="16px"
        data-cy-id="aggregate-portfolio-overview"
      >
        <Grid
          gapLg="4px"
          backgroundColor="noir"
          borderRadiusLg="x1"
          data-cy-id="aggregate-portfolio-performance"
        >
          <AggregatePortfolioPerformance
            aggregatePortfolio={aggregatePortfolio}
          />
        </Grid>
        <Grid
          backgroundColor="noir"
          padding="x2"
          borderRadius="x1"
          gap="16px"
          data-cy-id="aggregate-portfolio-snowflake"
        >
          <AggregatePortfolioSnowflake
            aggregatePortfolio={aggregatePortfolio}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
